// frontend/src/pages/members/members-page.tsx
// Read DESIGN.md before modifying this file. R3 centerpiece (spec Decisions
// 2/3/4): standalone workspace Members page with memberKind badges and two
// asymmetrically gated add-flows.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2, Plus, Search, Trash2, UserPlus, X } from 'lucide-react';
import { emit } from '@/lib/analytics';
import { FieldError } from '@/components/form/field-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCurrentRole } from '@/hooks/use-current-role';
import { useWorkspace } from '@/contexts/workspace-context';
import { RedactedEmail } from '@/components/redacted-email';
import { emailSchema } from '@/lib/form/schemas';
import { maskEmail } from '@/lib/mask-email';
import {
  useAssignMember,
  useCancelInvite,
  useChangeMemberRole,
  useInviteMember,
  useMembers,
  useRemoveMember,
} from './queries';
import { useOrgTeam } from '@/pages/org/org-team-queries';
import { PageContainer } from '@/components/page-container';

// 24h grace before a member's "Setup pending" chip appears (ported from the
// old MembersSection, Phase 91 Task 15).
const SETUP_PENDING_GRACE_MS = 24 * 60 * 60 * 1000;

const inviteSchema = z.object({
  email: z.string().trim().pipe(emailSchema),
  role: z.enum(['member', 'admin']),
});

type MemberKind = 'team' | 'workspace_scoped' | 'org_admin';

interface MemberDto {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  joinedAt: number;
  cliLastSeenAt: number | null;
  memberKind: MemberKind;
}

interface InviteDto {
  id: string;
  email: string;
  role: string;
  createdAt: string;
}

type MemberRow = {
  type: 'member';
  id: string;
  name: string;
  email: string;
  role: string;
  memberKind: MemberKind;
  date: string;
  rawDate: number;
  setupPending: boolean;
};
type InviteRow = {
  type: 'invite';
  id: string;
  email: string;
  role: string;
  date: string;
  rawDate: number;
};
type MembersTableRow = MemberRow | InviteRow;

function errorCode(body: unknown): string | undefined {
  return body && typeof body === 'object' && 'code' in body
    ? (body as { code?: string }).code
    : undefined;
}

function errorMessage(body: unknown, fallback: string): string {
  return body && typeof body === 'object' && 'userMessage' in body
    ? ((body as { userMessage?: string }).userMessage ?? fallback)
    : fallback;
}

export function MembersPage() {
  const { activeWorkspace, sessionScoped } = useWorkspace();
  const { role: sessionRole } = useCurrentRole();

  // Two independent role axes (spec Decision 4):
  //   org admin     -> everything, including Assign From Team
  //   workspace admin (WorkspaceMember.role) -> invite/manage workspace-scoped
  // Row rule (contract 2): assigned Team members are managed by org admins
  // only; workspace admins see memberKind='team' rows read-only.
  const isOrgAdmin = sessionRole === 'admin';
  const isWorkspaceAdmin = activeWorkspace?.role === 'admin';
  const canManage = isOrgAdmin || isWorkspaceAdmin;

  const orgId = activeWorkspace?.organizationPublicId ?? '';
  const wsId = activeWorkspace?.id;

  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'rawDate', desc: true },
  ]);

  // Invite To Workspace dialog state
  const [inviteOpen, setInviteOpen] = useState(false);

  // Assign From Team dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const { data, isLoading } = useMembers(activeWorkspace?.id, sessionScoped);
  // Team roster for the Assign From Team picker. Fetched only while the
  // dialog is open; cache shared with the org Team page (qk.org(orgId,'team')).
  const teamQuery = useOrgTeam(orgId || undefined, assignOpen);
  const inviteMember = useInviteMember(wsId ?? '');
  const assignMember = useAssignMember(wsId ?? '', orgId);
  const removeMember = useRemoveMember(wsId ?? '', orgId);
  const changeMemberRole = useChangeMemberRole(wsId ?? '', orgId);
  const cancelInvite = useCancelInvite(wsId ?? '');
  const inviteForm = useForm({
    defaultValues: { email: '', role: 'member' as 'member' | 'admin' },
    validators: { onChange: inviteSchema },
    onSubmit: async ({ value }) => {
      if (!wsId) return;
      inviteMember.mutate({ ...value, email: value.email.trim() }, {
        onSuccess: () => {
          if (wsId) emit('member_invited', { role: value.role, workspace_id: wsId });
          toast.success('Invite sent');
          inviteForm.reset();
          setInviteOpen(false);
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Failed to invite member'));
        },
      });
    },
  });

  const members = useMemo<MemberDto[]>(
    () =>
      isLoading
        ? []
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data?.members ?? []).map((m: any) => ({
            id: m.id,
            email: m.email,
            firstName: m.firstName,
            lastName: m.lastName,
            role: m.role,
            joinedAt: m.joinedAt,
            cliLastSeenAt: m.cliLastSeenAt,
            memberKind: (m.memberKind ?? 'team') as MemberKind,
          })),
    [data?.members, isLoading],
  );

  const pendingInvites = useMemo<InviteDto[]>(
    () =>
      isLoading
        ? []
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data?.invites ?? []).map((inv: any) => ({
            id: inv.id,
            email: inv.email,
            role: inv.role,
            createdAt: inv.createdAt,
          })),
    [data?.invites, isLoading],
  );

  const rows = useMemo<MembersTableRow[]>(() => {
    const memberRows: MembersTableRow[] = members.map((m) => ({
      type: 'member',
      id: m.id,
      name: m.firstName || m.lastName ? `${m.firstName || ''} ${m.lastName || ''}`.trim() : '-',
      email: m.email,
      role: m.role,
      memberKind: m.memberKind,
      date: new Date(m.joinedAt).toISOString(),
      rawDate: m.joinedAt,
      setupPending:
        m.cliLastSeenAt === null && Date.now() - m.joinedAt > SETUP_PENDING_GRACE_MS,
    }));
    const inviteRows: MembersTableRow[] = pendingInvites.map((inv) => ({
      type: 'invite',
      id: inv.id,
      email: inv.email,
      role: inv.role,
      date: inv.createdAt,
      rawDate: new Date(inv.createdAt).getTime(),
    }));
    return [...memberRows, ...inviteRows];
  }, [members, pendingInvites]);

  // Phase 91-03: admin invariant -- ported verbatim from MembersSection.
  const adminCount = members.filter((m) => m.role === 'admin').length;
  const totalMembers = members.length;
  const ADMIN_INVARIANT_TOOLTIP =
    'A workspace needs at least one admin. To change this role, first promote another member to admin, or invite someone new as admin.';

  // R2 enforces the Team-row wall server-side with TEAM_MEMBER_NOT_MANAGEABLE;
  // the UI hides the controls, but surface friendly copy if the action slips
  // through anyway (stale page, member kind changed underneath).
  const TEAM_NOT_MANAGEABLE_MESSAGE =
    'Assigned Team members are managed by organization admins.';

  useEffect(() => {
    if (teamQuery.error) {
      toast.error('Failed to load the team roster');
    }
  }, [teamQuery.error]);

  async function handleAssign(
    organizationMemberPublicId: string,
    role: 'member' | 'admin',
  ) {
    if (!activeWorkspace) return;
    setAssigningId(organizationMemberPublicId);
    assignMember.mutate(
      { organizationMemberPublicId, role },
      {
        onSuccess: () => {
          toast.success('Member assigned');
          setAssignOpen(false);
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Failed to assign member'));
        },
        onSettled: () => setAssigningId(null),
      },
    );
  }

  const handleRemove = useCallback((memberId: string) => {
    if (!activeWorkspace) return;
    removeMember.mutate(
      { memberId },
      {
        onSuccess: () => {
          toast.success('Member removed');
        },
        onError: (err) => {
          toast.error(
            errorCode(err) === 'TEAM_MEMBER_NOT_MANAGEABLE'
              ? TEAM_NOT_MANAGEABLE_MESSAGE
              : errorMessage(err, 'Failed to remove member'),
          );
        },
      },
    );
  }, [activeWorkspace, removeMember]);

  const handleRoleChange = useCallback((memberId: string, newRole: string) => {
    if (!activeWorkspace) return;
    changeMemberRole.mutate(
      { memberId, role: newRole },
      {
        onSuccess: () => {
          toast.success('Role updated');
        },
        onError: (err) => {
          toast.error(
            errorCode(err) === 'TEAM_MEMBER_NOT_MANAGEABLE'
              ? TEAM_NOT_MANAGEABLE_MESSAGE
              : errorMessage(err, 'Failed to update role'),
          );
        },
      },
    );
  }, [activeWorkspace, changeMemberRole]);

  const handleCancelInvite = useCallback((inviteId: string) => {
    if (!activeWorkspace) return;
    cancelInvite.mutate(
      { inviteId },
      {
        onSuccess: () => {
          toast.success('Invite cancelled');
        },
        onError: () => {
          toast.error('Failed to cancel invite');
        },
      },
    );
  }, [activeWorkspace, cancelInvite]);

  // GET /organizations/:orgId/team returns ALL org members with `source`
  // (since 2026-06-12); the picker filters to source='team' client-side
  // (assign-from-team assigns staff only).
  const assignable = (teamQuery.data?.members ?? []).filter(
    (t) => t.source === 'team' && !members.some((m) => m.email === t.email),
  );

  const columns = useMemo<ColumnDef<MembersTableRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) =>
          row.original.type === 'invite' ? (
            <span className="italic">Pending</span>
          ) : (
            row.original.name
          ),
        enableSorting: false,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <>
            <RedactedEmail email={row.original.email} />
            {row.original.type === 'member' && row.original.setupPending && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                Setup pending
              </span>
            )}
          </>
        ),
        enableSorting: false,
      },
      {
        id: 'role',
        accessorKey: 'role',
        header: 'Workspace Role',
        cell: ({ row }) => {
          if (row.original.type === 'invite') {
            return <span className="capitalize">{row.original.role}</span>;
          }

          const isLastAdmin = row.original.role === 'admin' && adminCount === 1;
          const isSoloMember = totalMembers === 1;
          const roleLocked = isLastAdmin || isSoloMember;
          const rowManageable =
            row.original.memberKind === 'org_admin'
              ? false
              : row.original.memberKind === 'team'
                ? isOrgAdmin
                : canManage;

          if (!rowManageable) {
            return <span className="capitalize">{row.original.role}</span>;
          }

          const selectNode = (
            <Select
              value={row.original.role}
              onValueChange={(val) => {
                if (val) handleRoleChange(row.original.id, val);
              }}
            >
              <SelectTrigger
                size="sm"
                className="w-24"
                disabled={roleLocked}
                data-testid={`role-select-${row.original.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          );

          if (!roleLocked) return selectNode;
          return (
            <Tooltip>
              <TooltipTrigger
                render={<span tabIndex={0} className="inline-block" />}
              >
                {selectNode}
              </TooltipTrigger>
              <TooltipContent>{ADMIN_INVARIANT_TOOLTIP}</TooltipContent>
            </Tooltip>
          );
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => {
          if (row.original.type === 'invite') {
            return canManage ? (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => handleCancelInvite(row.original.id)}
                aria-label="Cancel invite"
              >
                <X className="size-3.5" />
              </Button>
            ) : null;
          }

          const isLastAdmin = row.original.role === 'admin' && adminCount === 1;
          const isSoloMember = totalMembers === 1;
          const removeLocked = isLastAdmin || isSoloMember;
          const rowManageable =
            row.original.memberKind === 'org_admin'
              ? false
              : row.original.memberKind === 'team'
                ? isOrgAdmin
                : canManage;

          if (!rowManageable) return null;

          const buttonNode = (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={(e) => {
                if (e.currentTarget.getAttribute('aria-disabled') === 'true') {
                  return;
                }
                handleRemove(row.original.id);
              }}
              aria-disabled={removeLocked || undefined}
              className={removeLocked ? 'opacity-50 cursor-not-allowed' : undefined}
              aria-label="Remove member"
              data-testid={`remove-member-${row.original.id}`}
            >
              <Trash2 className="size-3.5" />
            </Button>
          );

          if (!removeLocked) return buttonNode;
          return (
            <Tooltip>
              <TooltipTrigger
                render={<span tabIndex={0} className="inline-block" />}
              >
                {buttonNode}
              </TooltipTrigger>
              <TooltipContent>{ADMIN_INVARIANT_TOOLTIP}</TooltipContent>
            </Tooltip>
          );
        },
        enableSorting: false,
        enableGlobalFilter: false,
      },
      {
        id: 'rawDate',
        accessorKey: 'rawDate',
        enableGlobalFilter: false,
      },
    ],
    [
      adminCount,
      canManage,
      handleCancelInvite,
      handleRemove,
      handleRoleChange,
      isOrgAdmin,
      totalMembers,
    ],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => `${row.type}-${row.id}`,
    state: {
      globalFilter: search,
      sorting,
      columnVisibility: { rawDate: false },
    },
    onGlobalFilterChange: setSearch,
    onSortingChange: setSorting,
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = String(filterValue ?? '').trim().toLowerCase();
      if (!q) return true;
      const original = row.original;
      return (
        original.email.toLowerCase().includes(q) ||
        (original.type === 'member' && original.name.toLowerCase().includes(q))
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <PageContainer variant="fill" className="gap-4 max-md:h-auto">
      <h1 className="text-xl font-semibold">Members</h1>
      {/* No flex-1: the card hugs its rows and only shrinks (min-h-0) when
          they would overflow the viewport, scrolling the table internally. */}
      <Card className="min-h-0 !overflow-visible">
        <CardContent className="flex flex-col gap-3 md:min-h-0 md:flex-1">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search members..."
                className="h-9 pl-8 text-sm"
              />
            </div>

            {isOrgAdmin && (
              <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                <DialogTrigger
                  render={
                    <Button size="sm" variant="outline" className="h-9">
                      <UserPlus className="size-3.5" />
                      Assign From Team
                    </Button>
                  }
                />
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Assign From Team</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      Pick a role to assign. It applies to this workspace only and
                      does not change the member's organization role.
                    </p>
                    {teamQuery.isLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="size-4 animate-spin" />
                        Loading team roster...
                      </div>
                    ) : assignable.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Every team member is already in this workspace.
                      </p>
                    ) : (
                      <ul className="max-h-72 space-y-2 overflow-y-auto no-visible-scrollbar">
                        {assignable.map((t) => (
                          <li key={t.publicId} className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm">
                                {t.displayName || maskEmail(t.email)}
                              </p>
                              <RedactedEmail
                                email={t.email}
                                className="text-xs text-muted-foreground"
                              />
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                              {assigningId === t.publicId && (
                                <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                              )}
                              <Select
                                value="unassigned"
                                onValueChange={(val) => {
                                  if (val === 'member' || val === 'admin') {
                                    handleAssign(t.publicId, val);
                                  }
                                }}
                                disabled={assigningId !== null}
                              >
                                <SelectTrigger
                                  size="sm"
                                  className="w-32"
                                  aria-label={`Assign ${t.displayName || t.email}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  <SelectItem value="member">Member</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {canManage && (
              <Dialog
                open={inviteOpen}
                onOpenChange={(o) => {
                  if (!o) {
                    inviteForm.reset(); // Rider 2: reset form on close so values don't persist
                  }
                  setInviteOpen(o);
                }}
              >
                <DialogTrigger
                  render={
                    <Button size="sm" variant="outline" className="h-9">
                      <Plus className="size-3.5" />
                      Invite To Workspace
                    </Button>
                  }
                />
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Invite To Workspace</DialogTitle>
                  </DialogHeader>
                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      void inviteForm.handleSubmit();
                    }}
                    className="space-y-4"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <inviteForm.Field name="email">
                        {(field) => (
                          <div className="flex-1">
                            <Input
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.target.value)}
                              onBlur={field.handleBlur}
                              placeholder="Email address"
                              type="email"
                              className="w-full"
                              autoFocus
                            />
                            <FieldError errors={field.state.meta.errors} />
                          </div>
                        )}
                      </inviteForm.Field>
                      <inviteForm.Field name="role">
                        {(field) => (
                          <div>
                            <Select
                              value={field.state.value}
                              onValueChange={(val) =>
                                field.handleChange(val as 'member' | 'admin')
                              }
                            >
                              <SelectTrigger className="w-full sm:w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <FieldError errors={field.state.meta.errors} />
                          </div>
                        )}
                      </inviteForm.Field>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      The role applies to this workspace only. It does not grant
                      any organization permissions.
                    </p>
                    <inviteForm.Subscribe
                      selector={(state) => ({
                        canSubmit: state.canSubmit,
                        inviteEmail: state.values.email,
                        isSubmitting: state.isSubmitting,
                      })}
                    >
                      {({ canSubmit, inviteEmail, isSubmitting }) => (
                        <Button
                          type="submit"
                          className="w-full"
                          disabled={
                            !wsId ||
                            !canSubmit ||
                            isSubmitting ||
                            inviteMember.isPending ||
                            !String(inviteEmail).trim()
                          }
                        >
                          {inviteMember.isPending && (
                            <Loader2 className="size-3.5 animate-spin" />
                          )}
                          Send Invite
                        </Button>
                      )}
                    </inviteForm.Subscribe>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="scrollable-table md:min-h-0 md:flex-1 md:[&_[data-slot=table-container]]:h-full">
            <Table className="table-fixed max-md:min-w-[32rem]">
              <colgroup>
                <col style={{ width: '26%' }} />
                <col style={{ width: '34%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <TableHeader className="sticky top-0 z-10 bg-card pointer-events-none">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={
                      row.original.type === 'invite' ? 'text-muted-foreground' : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={
                          cell.column.id === 'name' || cell.column.id === 'email'
                            ? 'truncate'
                            : undefined
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
